import Vue from 'vue';
import pendoService from '@/modules/externalScripts/services/pendoService';
import store from '@/store';
import { maybeStartSessionRecording } from '@/plugins/mixpanelSessionReplay';

const isAnalyticsDisabled = () => store.getters['BusinessStore/isAnalyticsDisabled'];
const isAnalyticsConfigReady = () => store.getters['BusinessStore/isAnalyticsConfigReady'];
const isImpersonating = () => store.getters['AuthStore/isImpersonating'];
const isIntegrationEnv = () => window.pov_config?.VUE_APP_ENV === 'integration';
const isMixpanelDisabled = () => isAnalyticsDisabled() || isIntegrationEnv();
const MAX_QUEUE_SIZE = 200;
const QUEUE_TTL_MS = 5 * 60 * 1000;

const nowMs = () => Date.now();
const withTimestamp = (data) => ({ ...data, queuedAt: nowMs() });
const pruneQueue = (queue) => queue.filter((item) => nowMs() - item.queuedAt <= QUEUE_TTL_MS);
const limitQueue = (queue) => (queue.length > MAX_QUEUE_SIZE ? queue.slice(queue.length - MAX_QUEUE_SIZE) : queue);
const enqueueItem = (queue, item) => limitQueue([...pruneQueue(queue), item]);

let isUserSet = false;
let eventsQueue = [];
let identifiedEventsQueue = [];
let pendingUsername = null;
let pendingUserProperties = null;

const logMixpanelEvent = (action, ...args) => {
  if (window.localStorage.getItem('log-all-mixpanle')) {
    const disabledLabel = isMixpanelDisabled() ? ' (DISABLED)' : '';
    console.log(`[Mixpanel${disabledLabel}] ${action}`, ...args);
  }
};

const AnalyticsPlugin = {
  install(app) {
    const enqueueEvent = (target, eventName, options) => {
      eventsQueue = enqueueItem(eventsQueue, withTimestamp({ target, eventName, options }));
    };

    const enqueueIdentifiedEvent = (eventName, options) => {
      identifiedEventsQueue = enqueueItem(identifiedEventsQueue, withTimestamp({ eventName, options }));
    };

    const applyUsername = (username) => {
      if (!username) {
        isUserSet = false;
        return;
      }
      const isMixpanelDisabledState = isMixpanelDisabled() || !window.mixpanel || typeof window.mixpanel.identify !== 'function';
      const isGADisabledState = isAnalyticsDisabled() || !window.ga || typeof window.ga !== 'function';

      if (isMixpanelDisabledState) {
        return;
      }
      isUserSet = true;
      logMixpanelEvent('identify', username);
      window.mixpanel.identify(username);

      if (!isGADisabledState) {
        window.ga('set', 'userId', username);
      }
    };

    const applyUserProperties = (properties) => {
      logMixpanelEvent('people.set', properties);
      if (isMixpanelDisabled()) return;
      if (!isUserSet) return;
      if (window.mixpanel?.people) {
        window.mixpanel.people.set(properties);
      }
    };

    const applyPendingIdentityData = () => {
      if (!isAnalyticsConfigReady()) {
        return;
      }
      if (isAnalyticsDisabled()) {
        pendingUsername = null;
        pendingUserProperties = null;
        return;
      }
      if (pendingUsername) {
        const username = pendingUsername;
        pendingUsername = null;
        applyUsername(username);
      }
      if (pendingUserProperties && isUserSet) {
        const properties = pendingUserProperties;
        pendingUserProperties = null;
        applyUserProperties(properties);
      }
    };

    const registerIdentitySuperProperties = () => {
      const staffUid = store.getters['AuthStore/staffUid'];
      const businessUid = store.getters['AuthStore/businessUid'];
      const partnerName = store.getters['BusinessStore/brandName'];
      if (!staffUid || !businessUid) {
        return;
      }
      app.prototype.$registerSuperProperties({
        partner_name: partnerName,
        staff_uid: staffUid,
        business_uid: businessUid,
      });
    };

    // eslint-disable-next-line no-param-reassign
    app.prototype.$track = function track(eventName, options = {}) {
      this.$trackMixpanel(eventName, options);
      this.$trackGA(eventName, options);
      this.$trackPendo(eventName, options);
    };

    // eslint-disable-next-line no-param-reassign
    app.prototype.$trackMixpanel = function trackMixpanel(eventName, options = {}) {
      const trackOptions = {
        ...options,
        impersonation: isImpersonating(),
      };

      if (!isAnalyticsConfigReady()) {
        enqueueEvent('mixpanel', eventName, options);
        return;
      }

      const isMixpanelDisabledState = isMixpanelDisabled() || !window.mixpanel;

      if (!isUserSet && !isMixpanelDisabledState) {
        enqueueIdentifiedEvent(eventName, options);
        return;
      }

      logMixpanelEvent(eventName, options);

      if (isMixpanelDisabledState) {
        return;
      }

      window.mixpanel.track(
        eventName,
        trackOptions,
        {
          transport: 'sendBeacon',
        },
      );
      maybeStartSessionRecording(eventName);
    };

    const flushEventsQueue = () => {
      if (!isAnalyticsConfigReady()) {
        return;
      }
      if (isAnalyticsDisabled()) {
        eventsQueue = [];
        identifiedEventsQueue = [];
        pendingUsername = null;
        pendingUserProperties = null;
        return;
      }

      const queued = pruneQueue(eventsQueue);
      eventsQueue = [];

      queued.forEach(({ target, eventName, options }) => {
        if (target === 'mixpanel') {
          app.prototype.$trackMixpanel(eventName, options);
          return;
        }
        if (target === 'ga') {
          app.prototype.$trackGA(eventName, options);
          return;
        }
        if (target === 'pendo') {
          app.prototype.$trackPendo(eventName, options);
        }
      });
    };

    const flushIdentifiedEventsQueue = () => {
      if (!isAnalyticsConfigReady()) {
        return;
      }
      if (!isUserSet) {
        return;
      }
      if (isMixpanelDisabled() || !window.mixpanel) {
        identifiedEventsQueue = [];
        return;
      }

      const queued = pruneQueue(identifiedEventsQueue);
      identifiedEventsQueue = [];

      queued.forEach(({ eventName, options }) => {
        app.prototype.$trackMixpanel(eventName, options);
      });
    };

    store.subscribe((mutation) => {
      if (mutation.type === 'BusinessStore/setAnalyticsConfig') {
        applyPendingIdentityData();
        registerIdentitySuperProperties();
        flushEventsQueue();
        flushIdentifiedEventsQueue();
      }
    });

    /*
     * INTERNAL: $trackGA - Only sends to Google Analytics (no postMessage)
     * Used when receiving events FROM iframes (Angular/Vue)
     */
    // eslint-disable-next-line no-param-reassign
    app.prototype.$trackGA = function trackGA(eventName, options = {}) {
      const gaEvent = {
        hitType: 'event',
        eventCategory: options.category || 'User Action',
        eventAction: eventName,
        eventLabel: options.label || '',
      };

      if (!isAnalyticsConfigReady()) {
        enqueueEvent('ga', eventName, options);
        return;
      }

      const isGADisabledState = isAnalyticsDisabled() || !window.ga;
      if (isGADisabledState) {
        return;
      }
      if (window.ga) {
        window.ga('send', gaEvent);
      }
    };

    /*
     * INTERNAL: $trackPendo - Only sends to Pendo (no postMessage)
     * Used when receiving events FROM iframes (Angular/Vue)
     */
    // eslint-disable-next-line no-param-reassign
    app.prototype.$trackPendo = function trackPendoInternal(eventName, options = {}) {
      if (!isAnalyticsConfigReady()) {
        enqueueEvent('pendo', eventName, options);
        return;
      }

      if (isAnalyticsDisabled()) {
        return;
      }
      if (window.pendo?.track && !pendoService.isPendoDenied()) {
        window.pendo.track(eventName, options);
      }
    };

    /**
     * Set username/user ID for analytics
     */
    // eslint-disable-next-line no-param-reassign
    app.prototype.$setUsername = function setUsername(username) {
      if (!isAnalyticsConfigReady()) {
        pendingUsername = username;
        return;
      }
      if (username) {
        applyUsername(username);
        applyPendingIdentityData();
        flushIdentifiedEventsQueue();
      } else {
        isUserSet = false;
        pendingUsername = null;
        pendingUserProperties = null;
      }
    };

    // eslint-disable-next-line no-param-reassign
    app.prototype.$setUserProperties = function setUserProperties(properties = {}) {
      if (!isAnalyticsConfigReady() || !isUserSet) {
        pendingUserProperties = properties;
        return;
      }
      applyUserProperties(properties);
    };

    /**
     * Register super properties - attached to all future events
     */
    // eslint-disable-next-line no-param-reassign
    app.prototype.$registerSuperProperties = function registerSuperProperties(properties = {}) {
      logMixpanelEvent('register', properties);
      if (isMixpanelDisabled()) return;
      if (window.mixpanel) {
        window.mixpanel.register(properties);
      }
    };

    let angularStoreSubscription = () => {};
    /**
     * Register user with Mixpanel after login
     * Sets identity and user properties (partner_name, staff_uid, business_uid)
     */
    // eslint-disable-next-line no-param-reassign
    app.prototype.$registerMixpanelUser = function registerMixpanelUser() {
      angularStoreSubscription();
      const isAngularLoaded = store.getters['angularStore/isAngularLoaded'];
      if (!isAngularLoaded) {
        angularStoreSubscription = store.subscribe((mutation) => {
          if (mutation.type === 'AngularStore/setIsAngularLoaded') {
            registerMixpanelUser();
          }
        });
        return;
      }
      angularStoreSubscription();
      registerIdentitySuperProperties();
    };

    /**
     * Track page view with English page name
     */
    // eslint-disable-next-line no-param-reassign
    app.prototype.$trackPageName = function trackPageName(pageNameEn) {
      if (!pageNameEn) return;
      store.dispatch('LayoutStore/setDocumentTitleEn', pageNameEn);

      if (isMixpanelDisabled()) return;

      this.$registerSuperProperties({ page_name: pageNameEn });
      this.$trackMixpanel('Page View', { page_name: pageNameEn });
    };

    window.addEventListener('message', (event) => {
      // IMPORTANT: Mirrors behavior from vue/src/plugins/analytics.js
      // If POV is hosting a micro-service iframe - get analytics events
      // Consider any modifications made here to be included in the Vue equivalent
      const eventData = event.data;

      // Handle analytics events from iframes (Angular/Vuetage)
      if (eventData.event === 'analyticsServiceEvent') {
        const data = eventData.data;

        // Handle different types of analytics calls
        switch (data.method) {
          case 'setUserProperties':
            app.prototype.$setUserProperties(data.properties);
            break;

          case 'setUsername':
            app.prototype.$setUsername(data.username);
            break;

          default:
            if (data.eventName) {
              const options = data.options || {};
              if (eventData.origin) options.source = eventData.origin;
              app.prototype.$track(data.eventName, options);
            }
            break;
        }
      }
    });
  },
};

Vue.use(AnalyticsPlugin);

export default AnalyticsPlugin;
